import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  clientApiService,
  MessageItem,
  MessageAttachment,
  ProjectLeadInfo,
} from "../../services/client-api.service";

function formatTime(t: number) {
  return new Date(t).toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(t: number) {
  const d = new Date(t);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Hoy";
  if (d.toDateString() === yesterday.toDateString()) return "Ayer";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
}

export function MessagesChatPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [projectLead, setProjectLead] = useState<ProjectLeadInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!projectId) return;
    try {
      const data = await clientApiService.listMessages(projectId);
      setMessages(data.messages);
      setProjectLead(data.projectLead);
      // Marcar leídas
      if (data.messages.some((m) => !m.isFromClient && !m.readAt)) {
        await clientApiService.markMessagesRead(projectId);
      }
    } catch (err: any) {
      setError(err?.message || "Error al cargar mensajes");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    load();
    // Polling cada 15s mientras está abierto
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const grouped = useMemo(() => {
    const groups: { date: string; messages: MessageItem[] }[] = [];
    let currentDate = "";
    for (const msg of messages) {
      const d = formatDate(msg.createdAt);
      if (d !== currentDate) {
        currentDate = d;
        groups.push({ date: d, messages: [] });
      }
      groups[groups.length - 1].messages.push(msg);
    }
    return groups;
  }, [messages]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const uploaded: MessageAttachment[] = [];
      for (const file of Array.from(files)) {
        if (file.size > 10 * 1024 * 1024) {
          throw new Error(`${file.name} excede 10MB`);
        }
        const presigned = await clientApiService.presignMessageUpload({
          filename: file.name,
          contentType: file.type || "application/octet-stream",
        });
        const res = await fetch(presigned.upload_url, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!res.ok) throw new Error(`Error al subir ${file.name}`);
        uploaded.push({
          url: presigned.public_url,
          name: file.name,
          type: file.type,
          size: file.size,
        });
      }
      setPendingAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: any) {
      setError(err?.message || "Error al subir adjunto");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!projectId) return;
    if (!newMessage.trim() && pendingAttachments.length === 0) return;

    setIsSending(true);
    setError(null);

    try {
      const sent = await clientApiService.sendMessage({
        projectId,
        content: newMessage.trim() || "(adjunto)",
        attachments: pendingAttachments.length > 0 ? pendingAttachments : undefined,
      });
      setMessages((prev) => [...prev, sent]);
      setNewMessage("");
      setPendingAttachments([]);
    } catch (err: any) {
      setError(err?.message || "Error al enviar mensaje");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isImage = (type: string) => type.startsWith("image/");
  const isPdf = (type: string) => type === "application/pdf";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] -mx-4 lg:-mx-6">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 lg:px-6 py-3 bg-white border-b border-slate-200">
        <Link
          to="/dashboard/messages"
          className="text-slate-500 hover:text-primary text-xl leading-none"
        >
          ←
        </Link>
        <div className="w-10 h-10 rounded-full bg-primary-light text-primary flex items-center justify-center font-bold">
          {projectLead?.name?.charAt(0).toUpperCase() ?? "A"}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 truncate">
            {projectLead?.name || "Arquitecto líder"}
          </p>
          <p className="text-xs text-slate-500">Arquitecto líder del proyecto</p>
        </div>
      </div>

      {/* Mensajes */}
      <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 bg-slate-50 space-y-6">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-3 opacity-40">💬</div>
            <p className="text-slate-500 text-sm">
              Aún no hay mensajes. Inicia la conversación con tu arquitecto.
            </p>
          </div>
        ) : (
          grouped.map((group) => (
            <div key={group.date}>
              <div className="text-center mb-3">
                <span className="bg-slate-200 text-slate-600 text-xs px-3 py-1 rounded-full">
                  {group.date}
                </span>
              </div>
              <div className="space-y-3">
                {group.messages.map((msg) => {
                  const isOwn = msg.isFromClient;
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[80%] lg:max-w-[65%] rounded-2xl px-4 py-2.5 ${
                          isOwn
                            ? "bg-primary text-white"
                            : "bg-white border border-slate-200 text-slate-900"
                        }`}
                      >
                        {!isOwn && (
                          <p className="text-xs font-semibold mb-1 opacity-70">
                            {msg.senderName}
                          </p>
                        )}
                        {msg.content && msg.content !== "(adjunto)" && (
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {msg.content}
                          </p>
                        )}
                        {msg.attachments.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {msg.attachments.map((att, i) => (
                              <a
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                {isImage(att.type) ? (
                                  <img
                                    src={att.url}
                                    alt={att.name}
                                    className="rounded-lg max-w-[240px] max-h-[240px] object-cover"
                                  />
                                ) : (
                                  <div
                                    className={`flex items-center gap-2 p-2 rounded-lg ${
                                      isOwn
                                        ? "bg-white/10"
                                        : "bg-slate-100"
                                    }`}
                                  >
                                    <span className="text-lg">
                                      {isPdf(att.type) ? "📄" : "📎"}
                                    </span>
                                    <span className="text-xs truncate flex-1">
                                      {att.name}
                                    </span>
                                  </div>
                                )}
                              </a>
                            ))}
                          </div>
                        )}
                        <div
                          className={`text-[10px] mt-1 text-right ${
                            isOwn ? "text-white/70" : "text-slate-400"
                          }`}
                        >
                          {formatTime(msg.createdAt)}
                          {isOwn && msg.readAt && " ✓✓"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Composer */}
      <div className="bg-white border-t border-slate-200 px-4 lg:px-6 py-3">
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {pendingAttachments.map((att, i) => (
              <div
                key={i}
                className="relative group bg-slate-100 rounded-lg px-2 py-1 flex items-center gap-2 text-xs"
              >
                {isImage(att.type) ? (
                  <img
                    src={att.url}
                    alt={att.name}
                    className="w-6 h-6 rounded object-cover"
                  />
                ) : (
                  <span>{isPdf(att.type) ? "📄" : "📎"}</span>
                )}
                <span className="truncate max-w-[120px]">{att.name}</span>
                <button
                  onClick={() => removeAttachment(i)}
                  className="text-red-500 hover:text-red-700 text-xs"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-2 mb-2">
            {error}
          </div>
        )}

        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isSending}
            className="p-2.5 rounded-lg text-slate-500 hover:bg-slate-100 transition disabled:opacity-50"
            title="Adjuntar archivo"
          >
            {isUploading ? "⏳" : "📎"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.doc,.docx"
            onChange={handleFileSelect}
            className="hidden"
          />
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Escribe un mensaje..."
            rows={1}
            maxLength={2000}
            disabled={isSending}
            className="flex-1 resize-none px-3 py-2.5 border border-slate-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none text-sm max-h-32"
            style={{ minHeight: "42px" }}
          />
          <button
            onClick={handleSend}
            disabled={isSending || (!newMessage.trim() && pendingAttachments.length === 0)}
            className="px-4 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50 shrink-0"
          >
            {isSending ? "⏳" : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}